import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * A one-shot deep link: read a param once, act on it, and drop it from the URL.
 *
 * Deliberately not `useTableParams` — that hook owns state a screen keeps, and
 * these params are the opposite. `?setup=PORKBUN`, `?link=1` and `?domain=<id>`
 * are instructions that have been carried out by the time the screen has
 * rendered; leaving them in the address would re-open the sheet on every
 * refresh. Three screens each wrote the same "clone the params, delete the key,
 * replace" block to say that.
 *
 * `consume` runs at most once per distinct value, and only for a value that is
 * actually there.
 */
export function useDeepLinkParam(key: string, consume: (value: string) => void): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key);
  const handled = useRef<string | null>(null);
  const latest = useRef(consume);
  latest.current = consume;

  useEffect(() => {
    if (!value || handled.current === value) return;
    handled.current = value;
    latest.current(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [key, value, setSearchParams]);
}
